import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { validateEmail } from '../validation/authSchemas';
import { useFormValidation } from '../hooks/useFormValidation';
import { authService } from '../services/authService';
import { BrandLockup } from './BrandLockup';

interface EmailCheckFormProps {
  onEmailExists: (email: string) => void;
  onEmailNew: (email: string) => void;
}

export const EmailCheckForm: React.FC<EmailCheckFormProps> = ({
  onEmailExists,
  onEmailNew,
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { errors, validateField, clearError, hasErrors } = useFormValidation();

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('email', value, validateEmail);
    } else {
      clearError('email');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (hasErrors || !email.trim()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await authService.checkEmail(email);
      
      if (response.data.exists) {
        onEmailExists(email);
      } else {
        onEmailNew(email);
      }
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Failed to check email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        background: 'linear-gradient(135deg, #173B67 0%, #3478C8 100%)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <BrandLockup height={48} suffix="Admin" sx={{ mb: 2, mx: 'auto', display: 'flex' }} />
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Admin sign in
        </Typography> 
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Enter your admin email to continue
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Admin Email Address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoComplete="email"
            autoFocus
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || hasErrors || !email.trim()}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            We'll check if you have admin access or help you create an admin account
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};
